import { IEKGEvent } from '../store/ekgImport.types'
import { IItem } from '../../timeline/store/timeline.types'
import { TabConstants } from '../../../components/tabs/Tabs.constants'
import { TimelineConstants } from '../../timeline/store/timeline.constants'
import { IIntubationState } from '../../intubation/store/intubation.types'
import { IOptions } from '../../../common/common.types'
import { INemsisSnomedLookup } from '../../../components/procedure/store/procedure.types'
import { generateClientCorrelationId } from '../../nemsisData/utils/customElements.util'
import { getUpdatedProcedureStateWithNemsisCode, handleNemsisCodeError } from '../../../utilities/nemsisCode.util'

interface CreateIntubationProcedureProps {
  procedureEvent: IEKGEvent
  intubationOptions: IOptions
  ptaCutoff: Date | null
  nemsisCodeOptions: INemsisSnomedLookup[]
}

export interface IntubationProcedureObject {
  intubationState: IIntubationState
  timelineItem: IItem
}

const createIntubationProcedure = (props: CreateIntubationProcedureProps): IntubationProcedureObject => {
  const { procedureEvent, intubationOptions, ptaCutoff, nemsisCodeOptions } = props

  const clientEventId = (procedureEvent.eventVitals.clientEventId as number).toString()
  const vsAction = procedureEvent.eventVitals.vsAction
  const isEkgImport = procedureEvent.eventVitals.isEkgImport

  const itemComments = procedureEvent.procedureComments ?? ''
  const intubationMethodOption = intubationOptions.find(
    (option) => (option.id ?? '').toString() === procedureEvent.procedureAction
  )
  const itemSubHeader = `By , Size: , Distance, Attempts: 1, Success: Yes, Comments: ${itemComments}.`

  const eventDate = new Date(procedureEvent.dtDevice)
  const isPTA = !!ptaCutoff && eventDate < ptaCutoff

  const clientCorrelationId = generateClientCorrelationId(
    nemsisCodeOptions,
    TimelineConstants.INTUBATION_PROCEDURE,
    intubationMethodOption?.name || '',
    {
      filter1: null,
      filter2: null,
      filter3: null,
    },
    clientEventId
  )

  const timelineItem: IItem = {
    key: clientEventId,
    clientCorrelationId,
    itemType: 'Procedures',
    event: 'Intubation',
    grid: true,
    labelHeaderFlag: isPTA ? 'PTA' : '',
    labelHeader: procedureEvent.eventVitals.time,
    subHeader: procedureEvent.eventVitals.date,
    eventSubHeader: itemSubHeader,
    rowActionType: TimelineConstants.SET_INTUBATION_PROCEDURE_ITEM,
    defaultHref: TabConstants.INTUBATION_PROCEDURE,
    date: new Date(procedureEvent.dtDevice),
    clientEventId: clientEventId,
    ...(vsAction && { vsAction: vsAction }),
    ...(isEkgImport && { isEkgImport: isEkgImport }),
  }

  let intubationState: IIntubationState = {
    key: clientEventId,
    date: procedureEvent.eventVitals.date,
    time: procedureEvent.eventVitals.time,
    assessedBy: null,
    successfulAttempt: true,
    pta: isPTA,
    attempts: '1',
    size: '',
    distance: '',
    distanceUnit: null,
    method: intubationMethodOption ?? null,
    securedVia: null,
    verifiedBy: null,
    verificationVia: [],
    airwayPlacementTechnique: [],
    airwayPlacementTechniquePNNV: null,
    rSIProtocol: null,
    mallampati: null,
    sellickManeuver: null,
    laryngoscopicGrade: null,
    styletUsed: null,
    cuffFill: null,
    indications: [],
    cuffFillQty: '',
    laryngoscopeBlade: null,
    decisiontoManageTime: '',
    decisiontoManageDate: '',
    decisiontoManageDateTime: '',
    abandonedTime: '',
    abandonedDate: '',
    abandonedDateTime: '',
    airwayComplications: [],
    airwayComplicationsPNNV: null,
    procedureComplications: [],
    response: null,
    responsePNNV: null,
    authorization: null,
    comments: itemComments,
    addToTimeline: true,
    updateInputRows: false,
    type: 'Intubation',
    rolePerforming: null,
    rolePerformingPNNV: null,
    isEkgImport: 1,
    intubationDefaults: null,
    reasonsForFailure: [],
    nemsisCode: null,
    procedurePerformedByAnotherUnit: null,
    procedurePerformedByAnotherUnitPNNV: null,
  }

  try {
    intubationState = getUpdatedProcedureStateWithNemsisCode({
      procedureState: intubationState,
      actionField: (s) => s.method.name,
      filter1Field: (s) => s.verificationVia?.id?.toString(), // verification via (any value)
      filter2Field: (s) => s.rSIProtocol?.id?.toString(), // rsi protocol (0-no or 1-yes)
      filter3Field: (s) => s.securedVia?.id?.toString(), // tube secured via (amb.tb_prtube_secured_via)
      nemsisCodeOptions,
    })
  } catch (error: unknown) {
    intubationState = handleNemsisCodeError(error, intubationState)
  }

  const newIntubationProcedureObject: IntubationProcedureObject = {
    intubationState,
    timelineItem,
  }

  return newIntubationProcedureObject
}

export default createIntubationProcedure
